import { TrafficType } from '@campaign-portal/namespace/common/enums';
import { LinkDomain } from '@campaign-portal/namespace/entities/link-domains/specs';
import { exist } from '@campaign-portal/namespace/common/id';
import { CampaignShortLink } from '@campaign-portal/namespace/entities/campaigns/specs';

const HEX_BASE = 16;

export const URL_PATTERN = /https?:\/\/[a-zA-Z0-9$\-_.+!*'/&?=:%#]+/g;

export const SHORT_LINK_PATTERN = (host?: string): RegExp => {
	const hostPattern = host || `(?<host>[^\\]]*)`;

	return new RegExp(`\\[${hostPattern}\\]\\((?<url>https?://[a-z0-9$\\-_.+!*'/&?=:%#]+)\\)`, 'gi');
};

const LINK_DOMAIN_TEMPLATE = (host: string): string => {
	return `https://${host}/xxxxxx`;
};

// eslint-disable-next-line sonarjs/redundant-type-aliases
type UrlId = string;

export class ChannelMessageToHTMLtoSerializer {
	private message = '';
	private readonly urlsMap = new Map<UrlId, string>();

	constructor(message: string) {
		this.message = message;
	}

	get value(): string {
		return this.message;
	}

	addVariableStyling(variableList: { fullMatch: string; appearance: string }[]): this {
		for (const v of variableList) {
			const pattern = new RegExp(v.fullMatch, 'g');
			this.message = this.message.replace(
				pattern,
				`<span class="variable" contenteditable="false">${v.appearance}</span></span>`
			);
		}
		return this;
	}

	addUrlStyling(): this {
		this.message = this.message.replaceAll(URL_PATTERN, `<a target="_blank" href="$&" class="link">$&</a>`);
		return this;
	}

	addLinkDomains(domains: LinkDomain<exist>[], shortLink?: CampaignShortLink): this {
		if (!domains.length) {
			this.message = shortLink?.shortUrl
				? this.message.replace(SHORT_LINK_PATTERN(), shortLink.shortUrl)
				: this.message;
			return this;
		}

		for (const { id, host } of domains) {
			const templateLink = shortLink && shortLink.domainId === id ? shortLink.shortUrl : LINK_DOMAIN_TEMPLATE(host);

			this.message = this.message.replace(SHORT_LINK_PATTERN(host), templateLink);
		}

		return this;
	}

	addUnbreakableSpaces(): this {
		this.message = this.message.replaceAll(/ {2}/g, '&nbsp; ');
		return this;
	}

	changeFontDecorationTag(viberSymbol: string, htmlTag: string): this {
		const regex = new RegExp(`${viberSymbol}(.*?)${viberSymbol}`, 'g');
		this.message = this.message.replace(regex, `<${htmlTag}>$1</${htmlTag}>`);
		return this;
	}

	replaceUrlsToIds(): this {
		this.message = this.message.replaceAll(URL_PATTERN, (url) => {
			const id = `URL-ID={${Math.random().toString(HEX_BASE).split('.')[1]}}`;
			this.urlsMap.set(id, url);
			return id;
		});
		return this;
	}

	restoreUrlsFromIds(): this {
		for (const [id, url] of this.urlsMap) {
			this.message = this.message.replaceAll(id, url);
		}
		return this;
	}
}

export function convertToHTMLFormat(
	message: string,
	channel: TrafficType,
	variableList: { fullMatch: string; appearance: string }[],
	domains: LinkDomain<exist>[] = [],
	shortLink?: CampaignShortLink
): string {
	const channelTemplateMessage = new ChannelMessageToHTMLtoSerializer(message)
		.addVariableStyling(variableList)
		.addLinkDomains(domains, shortLink)
		.addUrlStyling()
		.addUnbreakableSpaces();

	if (channel !== TrafficType.SMS) {
		channelTemplateMessage
			.replaceUrlsToIds()
			.changeFontDecorationTag(String.raw`\*`, 'b')
			.changeFontDecorationTag('_', 'i')
			.changeFontDecorationTag('~', 'strike')
			.restoreUrlsFromIds();
	}
	return channelTemplateMessage.value;
}
